import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Stack,
    TextField,
    Avatar,
    IconButton,
    Tooltip,
    Paper,
    Button,
    InputAdornment,
    useTheme,
} from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

import PromptPopover from './PromptPopover';
import {
    SimplePromptsIcon,
    DataIcon,
    DocIcon,
    CodeIcon,
    BookmarkIcon,
    TestCaseIcon,
} from '../assets/SvgIcons';
import {
    setSelectedPromptItem,
    setSelectedTileId,
} from '../reducers/uiReducer';

import {
    setPromptBarActive,
} from '../reducers/promptBarReducer';

import { selectChatContext } from '../reducers/messagesReducer';
import { rgb } from 'd3';


const PromptBar = ({ config, callBack, paperHeight, setPaperHeight }) => {
    const [inputValue, setInputValue] = useState('');
    const theme = useTheme();
    const textFieldRef = useRef(null);
    const dispatch = useDispatch();

    //const [paperHeight, setPaperHeight] = useState(115);
    const dragRef = useRef(null);
    const isDraggingRef = useRef(false);

    const startYRef = useRef(null);
    const startHeightRef = useRef(null);

    const promptItems = useSelector((state) => state.ui.promptItems);
    const isPromptBarActive = useSelector(
        (state) => state.promptBar.isPromptBarActive,
    );
    const chatContext = useSelector(selectChatContext);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const id = open ? 'flyout-popover' : undefined;

    const handleFlyoutOpen = (event) => setAnchorEl(event.currentTarget);
    const handleFlyoutClose = () => setAnchorEl(null);
    const handlePromptItem = (item) => {
        dispatch(setSelectedTileId(item.type));
        dispatch(setSelectedPromptItem(item));
    };

    const handleRunPrompt = (prompt) => {

        const text = prompt?.promptText || inputValue;
        setMinHeight();

        if (text.trim()) {
            callBack(text, prompt);
            setInputValue('');
        } else if (promptItems.length > 0) {
            callBack(text, prompt);
            setInputValue('');
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleRunPrompt();
        }
    };

    const handleFocus = () => dispatch(setPromptBarActive(true));
    const handleBlur = (e) => {
        console.log("hit")
        dispatch(setPromptBarActive(false));
        if (config.onBlur) config.onBlur(e);
    };



    useEffect(() => {
        if (isPromptBarActive && textFieldRef.current) {
            requestAnimationFrame(() => textFieldRef.current.focus());
        }
    }, [isPromptBarActive]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDraggingRef.current) return;

            const dy = startYRef.current - e.clientY;
            const newHeight = startHeightRef.current + dy;

            if (newHeight > 80 && newHeight < 300) {
                setPaperHeight(newHeight);
            }
        };

        const handleMouseUp = () => {
            isDraggingRef.current = false;
            document.body.style.userSelect = ''; // Reset selection
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);



    const getIconByType = (type) => {
        switch (type) {
            case 'analyze_dataset':
                return <DataIcon />;
            case 'analyze_document':
                return <DocIcon />;
            case 'code_assistant':
                return <CodeIcon />;
            case 'manage_saved_prompts':
                return <BookmarkIcon />;
            case 'generate_test_cases':
                return <TestCaseIcon />;
            default:
                return <DocIcon />;
        }
    };

    const isPromptItemsEmpty =
        !promptItems || Object.keys(promptItems).length === 0;

    const backgroundLayer1 =
        theme.palette.mode === 'dark'
            ? 'linear-gradient(to bottom, #1e1e1e, #2b2b2b)'
            : 'linear-gradient(to bottom, rgb(200, 237, 255), rgb(228, 236, 255), rgb(158, 195, 254))';

    const backgroundLayer2 =
        theme.palette.mode === 'dark'
            ? 'linear-gradient(to bottom,rgb(48, 48, 48),rgb(63, 63, 63))'
            : 'linear-gradient(to bottom, rgb(150, 220, 255), rgb(178, 216, 255), rgb(108, 175, 254))';

    const MIN_HEIGHT = 96;
    const MAX_HEIGHT = 300;

    const toggleExpanded = () => {
        setPaperHeight(prev => prev === MAX_HEIGHT ? MIN_HEIGHT : MAX_HEIGHT);
    };

    // set MIN_HEIGHT
    const setMinHeight = () => {
        setPaperHeight(MIN_HEIGHT);
    }


    useEffect(() => {
        if (!textFieldRef.current) return;

        requestAnimationFrame(() => {
            const textarea = textFieldRef.current;
            if (!textarea) return;

            const shouldExpand = textarea.scrollHeight > textarea.clientHeight;

            if (shouldExpand && paperHeight < MAX_HEIGHT) {
                setPaperHeight(MAX_HEIGHT);
            }
        });
    }, [inputValue]);
    return (
        <Box
            ref={dragRef}

            elevation={2}
            sx={{
                position: 'relative',
                borderRadius: '0px 0px 8px 8px',
                height: paperHeight,
                transition: 'height 0.3s ease-in-out',
                minHeight: 100,
                maxHeight: 300,
                pb: 1,
                //transition: isDraggingRef.current ? 'none' : 'height 0.2s ease-in-out',
                overflow: 'hidden',
            }}
        >
            <Box
                // onMouseDown={(e) => {
                //     isDraggingRef.current = true;
                //     startYRef.current = e.clientY;
                //     startHeightRef.current = paperHeight;
                // }}
                sx={{
                    height: 2,
                    //cursor: 'ns-resize',
                    backgroundColor: theme.palette.divider,
                    width: '100%',
                    position: 'absolute',
                    top: 0,
                    zIndex: 10,
                    //borderTopLeftRadius: 8,
                    //borderTopRightRadius: 8,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '&:hover': {
                        //backgroundColor: theme.palette.primary.light,
                    },
                }}
            >
                {/* Toggle Notch Button */}
                <Box
                    onClick={(e) => {
                        e.stopPropagation(); // prevent drag start
                        toggleExpanded();
                    }}
                    sx={{
                        width: 50,
                        height: 8,
                        backgroundColor: theme.palette.mode === 'dark' ? '#888' : 'rgba(0, 91, 177, 0.24)',
                        //add shadow
                        boxShadow: theme.palette.mode === 'dark' ? '0 0 3px rgb(0, 0, 0)' : '0 0 3px rgba(0, 0, 0, 0.4)',
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease',
                        '&:hover': {
                            transform: 'scale(1.1)',
                            backgroundColor: theme.palette.primary.main,
                        },
                    }}
                />
            </Box>
            {/* Background Layers */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: backgroundLayer1,
                    opacity: 1.0,
                    zIndex: 1,
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: backgroundLayer2,
                    opacity: isPromptBarActive ? 1 : 0,
                    transition: 'opacity .25s ease-in-out',
                    zIndex: 2,
                }}
            />

            {/* Foreground Content */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}

                sx={{
                    position: 'relative',
                    zIndex: 3,
                    p: 2,
                    pt: 4,
                    borderRadius: '0px 0px 8px 8px',
                    width: '100%',
                    height: '100%',
                }}
            >

                {/* Prompt Menu Icon */}
                <Box sx={{ top: 10 }} >
                    <Tooltip title="Quick Prompts">
                        <Box
                            sx={{
                                height: 60,
                                width: 60,
                                p: 1,
                                cursor: 'pointer',
                                transition: 'transform 0.3s ease',
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                },
                                '&:active': {
                                    backgroundColor: theme.palette.action.selected,
                                },
                            }}
                            onClick={handleFlyoutOpen}
                        >
                            <SimplePromptsIcon />
                        </Box>
                    </Tooltip>
                </Box>

                <PromptPopover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    handleClose={handleFlyoutClose}
                    handleRunPromptCallback={handleRunPrompt}
                />

                {/* Prompt Input */}
                <Box sx={{ flexGrow: 1, height: '100%', minHeight: 60, }}>
                    <TextField
                        //inputRef={textFieldRef}
                        fullWidth
                        multiline
                        value={inputValue}

                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyPress}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        placeholder={config.placeholder}
                        {...config.inputProps}
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            height: '100%',
                            overflow: 'hidden',
                            display: 'flex',

                            flexDirection: 'column',
                            '& .MuiInputBase-root': {
                                backgroundColor: theme.palette.background.paper,
                                flexGrow: 1,
                                display: 'flex',
                                alignItems: 'stretch',
                                padding: 0,
                                borderRadius: '2px',
                                boxShadow: 'none',
                                //border: '1px solid #ccc',
                                '&:hover': {
                                    //borderColor: '#aaa',
                                },
                                '&.Mui-focused': {
                                    //borderColor: '#ccc',
                                    boxShadow: 'none',
                                },

                                '& textarea': {
                                    height: '100% !important',
                                    resize: 'none',
                                    overflow: 'auto',
                                    paddingTop: '18px',
                                    paddingLeft: '18px',
                                    boxSizing: 'border-box',

                                    border: 'none',
                                    outline: 'none',
                                },
                            }
                        }}
                        InputProps={{
                            inputRef: textFieldRef,
                            inputComponent: 'textarea',
                            sx: {
                                overflowY: 'auto',
                                resize: 'none',
                                height: '100%',
                            },
                            startAdornment: !isPromptItemsEmpty && (
                                <InputAdornment position="start" sx={{ ml: .5, mt: 1.75, mr: 0 }}>
                                    <AnimatePresence>
                                        {Object.values(promptItems).map((item) => (
                                            <motion.div
                                                key={item.id}
                                                layout
                                                initial={{ opacity: 0, x: 20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, x: -20 }}
                                                transition={{ duration: 0.2, ease: 'easeInOut' }}
                                            >
                                                <IconButton onClick={() => handlePromptItem(item)}>
                                                    <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
                                                        {getIconByType(item.type)}
                                                    </Avatar>
                                                </IconButton>
                                            </motion.div>
                                        ))}
                                    </AnimatePresence>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {/* Submit Button */}
                <Button
                    onClick={() => handleRunPrompt()}
                    color="primary"
                    disabled={!inputValue.trim() && (!promptItems || Object.keys(promptItems).length === 0)}
                    sx={{
                        transition: 'transform 0.3s ease',
                        transform: 'scale(0.9)',
                        '&:hover': {
                            transform: 'scale(1.0)',
                            color: theme.palette.primary.main,
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: 54,
                        },
                    }}
                >
                    <PlayArrow />
                </Button>

            </Stack >
        </Box >
    );
};

export default PromptBar;
